import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Square } from 'lucide-react';
import { Button } from '../components/ui/button';

const Staff = () => {
  const { t } = useTranslation();
  const { token, API, user } = useContext(AuthContext);
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeShift, setActiveShift] = useState(null);

  useEffect(() => {
    fetchShifts();
    fetchUsers();
  }, []);

  const fetchShifts = async () => {
    try {
      const res = await axios.get(`${API}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(res.data);
      const active = res.data.find(s => s.user_id === user.id && !s.end_time);
      setActiveShift(active);
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users');
    }
  };

  const handleStartShift = async () => {
    try {
      await axios.post(`${API}/shifts`, { user_id: user.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      fetchShifts();
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleEndShift = async () => {
    if (!activeShift) return;
    try {
      await axios.put(`${API}/shifts/${activeShift.id}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.success'));
      setActiveShift(null);
      fetchShifts();
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const getUserName = (userId) => {
    return users.find(u => u.id === userId)?.username || 'Unknown';
  };

  return (
    <div className="staff">
      <div className="section-header">
        <h2 className="section-title">{t('staff.staff')}</h2>
        {activeShift ? (
          <Button onClick={handleEndShift} variant="destructive" data-testid="end-shift-btn">
            <Square size={16} /> {t('staff.endShift')}
          </Button>
        ) : (
          <Button onClick={handleStartShift} data-testid="start-shift-btn">
            <Play size={16} /> {t('staff.startShift')}
          </Button>
        )}
      </div>

      {activeShift && (
        <div className="active-shift-card" data-testid="active-shift">
          <div className="active-badge">{t('staff.activeShift')}</div>
          <div>Started: {new Date(activeShift.start_time).toLocaleString()}</div>
        </div>
      )}

      <div className="card mt-4">
        <h3 className="mb-4">{t('staff.shiftHistory')}</h3>
        <table>
          <thead>
            <tr>
              <th>{t('staff.staff')}</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((shift) => {
              const duration = shift.end_time
                ? Math.round((new Date(shift.end_time) - new Date(shift.start_time)) / 1000 / 60 / 60 * 10) / 10
                : null;
              return (
                <tr key={shift.id} data-testid={`shift-row-${shift.id}`}>
                  <td data-testid="shift-user">{getUserName(shift.user_id)}</td>
                  <td>{new Date(shift.start_time).toLocaleString()}</td>
                  <td>{shift.end_time ? new Date(shift.end_time).toLocaleString() : 'Active'}</td>
                  <td data-testid="shift-duration">{duration ? `${duration}h` : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        .staff {
          width: 100%;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
        }

        .active-shift-card {
          background: #dbeafe;
          border: 2px solid #3b82f6;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          color: #1e40af;
        }

        .active-badge {
          font-weight: 700;
          font-size: 14px;
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
};

export default Staff;
